import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { evaluationAPI, categoryAPI } from '../../api/services';
import { Category, Evaluation, User } from '../../types';

const ViewEvaluation: React.FC = () => {
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const id = routeId || searchParams.get('evaluationId');

  useEffect(() => {
    if (id) {
      fetchEvaluation(id);
    }
  }, [id]);

  const fetchEvaluation = async (evaluationId: string) => {
    try {
      setLoading(true);
      const response = await evaluationAPI.getEvaluationById(evaluationId);
      const evalData = response.data;

      console.log('[VIEW EVAL] Loaded evaluation:', evalData);
      setEvaluation(evalData);

      // Fetch categories for the level group
      try {
        const catResponse = await categoryAPI.getCategoriesByLevelGroup(evalData.levelGroup, evalData.evaluationTypeId);
        console.log('[VIEW EVAL] Categories loaded:', catResponse.data);
        setCategories(catResponse.data || []);
      } catch (catError) {
        console.error('[VIEW EVAL] Error loading categories:', catError);
        // Set empty categories array to prevent crashes
        setCategories([]);
      }
    } catch (err: any) {
      console.error('[VIEW EVAL] Error loading evaluation:', err);
      setError(err.response?.data?.message || 'Failed to load evaluation');
    } finally {
      setLoading(false);
    }
  };

  const calculateWeightForSubcategory = (categoryIndex: number, subIndex: number): number => {
    // Guard clause: ensure categories are loaded
    if (!categories || categories.length === 0) {
      console.warn('[VIEW EVAL] Categories not loaded yet');
      return 0;
    }

    const category = categories[categoryIndex];
    if (!category) {
      console.warn('[VIEW EVAL] Category not found at index', categoryIndex);
      return 0;
    }

    if (!category.subCategories || !category.subCategories[subIndex]) {
      console.warn('[VIEW EVAL] Subcategory not found', { categoryIndex, subIndex });
      return 0;
    }

    return category.subCategories[subIndex]?.weight || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-1">{error || 'Evaluation not found'}</p>
          <p className="text-xs text-gray-400">The evaluation may have been removed or you don't have access.</p>
        </div>
      </div>
    );
  }

  const user = evaluation.userId as User;
  const isSubmitted = evaluation.status === 'submitted';

  return (
    <div className="bg-white">
      <div className="px-6">
        {/* Header */}
        <div className="py-4 border-b border-gray-100 mb-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{evaluation.evaluationPeriod} — Self Evaluation</h1>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                <span>{evaluation.levelGroup}</span>
                <div className="w-px h-3 bg-gray-200" />
                <span>Score: <span className="text-blue-600 font-medium">{evaluation.totalScore?.toFixed(2)}</span> / 5.00</span>
                <div className="w-px h-3 bg-gray-200" />
                <span>{evaluation.performanceRating}</span>
                {isSubmitted && evaluation.submittedAt && <><div className="w-px h-3 bg-gray-200" /><span>Submitted {new Date(evaluation.submittedAt).toLocaleDateString()}</span></>}
              </div>
            </div>
            <span className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${isSubmitted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {isSubmitted ? 'Submitted' : 'Draft'}
            </span>
          </div>
          {evaluation.showAdminScoresToEmployee && evaluation.adminTotalScore && (
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
              <span>Admin Score: <span className="text-blue-600 font-medium">{evaluation.adminTotalScore?.toFixed(2)}</span> / 5.00</span>
              <div className="w-px h-3 bg-gray-200" />
              <span>Admin Rating: {evaluation.adminPerformanceRating}</span>
              {evaluation.adminReviewedAt && <><div className="w-px h-3 bg-gray-200" /><span>Reviewed {new Date(evaluation.adminReviewedAt).toLocaleDateString()}</span></>}
            </div>
          )}
        </div>

        {/* Category Scores */}
        <div className="space-y-6">
          {evaluation.categoryScores.map((categoryScore, catIndex) => {
            const category = categories.find(c => c.name === categoryScore.categoryName);
            const hasAdminScores = evaluation.showAdminScoresToEmployee && categoryScore.scores.some(s => s.adminScore !== undefined && s.adminScore !== null);

            // Skip rendering if category not found
            if (!category) {
              console.warn('[VIEW EVAL] Category not found for:', categoryScore.categoryName);
              return null;
            }

            return (
              <div key={catIndex} className="bg-white rounded-xl shadow-card p-6">
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-brand-gray-900">
                    {categoryScore.categoryName}
                  </h2>
                  <div className="flex gap-4 mt-2 text-sm">
                    <p className="text-brand-gray-600">
                      Your Score: <span className="font-semibold">{categoryScore.totalWeightedScore?.toFixed(2)}</span>
                    </p>
                    {hasAdminScores && categoryScore.adminTotalWeightedScore && (
                      <p className="text-blue-600">
                        Admin Score: <span className="font-semibold">{categoryScore.adminTotalWeightedScore?.toFixed(2)}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  {categoryScore.scores.map((score, subIndex) => {
                    // Find the subcategory weight from the matched category
                    const subCategory = category.subCategories[subIndex];
                    const weight = subCategory?.weight || 0;

                    return (
                      <div key={subIndex} className="border border-brand-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold text-brand-gray-900">
                            {score.subCategoryName}
                          </h3>
                          <span className="text-sm text-brand-gray-500">
                            Weight: {(weight * 100).toFixed(0)}%
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {/* Employee Score */}
                          <div>
                            <label className="block text-sm font-medium text-brand-gray-700 mb-1">
                              Your Score
                            </label>
                            <div className="text-2xl font-bold text-brand-primary-600">
                              {score.score || 'N/A'}
                            </div>
                            {score.evidence && (
                              <div className="mt-2">
                                <p className="text-xs text-brand-gray-500 mb-1">Evidence:</p>
                                <p className="text-sm text-brand-gray-700 bg-brand-gray-50 p-2 rounded">
                                  {score.evidence}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Admin Score */}
                          {evaluation.showAdminScoresToEmployee && score.adminScore !== undefined && score.adminScore !== null && (
                            <div className="border-l border-brand-gray-200 pl-4">
                              <label className="block text-sm font-medium text-blue-700 mb-1">
                                Admin Score
                              </label>
                              <div className="text-2xl font-bold text-blue-600">
                                {score.adminScore}
                              </div>
                              {score.adminComments && (
                                <div className="mt-2">
                                  <p className="text-xs text-blue-500 mb-1">Admin Comments:</p>
                                  <p className="text-sm text-brand-gray-700 bg-blue-50 p-2 rounded">
                                    {score.adminComments}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        {!isSubmitted && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <button
              onClick={() => {
                const typeId = typeof evaluation.evaluationTypeId === 'string'
                  ? evaluation.evaluationTypeId
                  : (evaluation.evaluationTypeId as any)?._id || '';
                navigate(`/employee/evaluation?period=${encodeURIComponent(evaluation.evaluationPeriod)}&evaluationId=${evaluation._id}${typeId ? `&typeId=${typeId}` : ''}`);
              }}
              className="px-6 py-2 bg-brand-primary-600 text-white font-medium rounded-lg hover:bg-brand-primary-700 transition-colors"
            >
              Continue Editing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewEvaluation;
